import { compMissingFields } from "./ReadinessGate";

/**
 * TOP THREE TICKED ONCE (C1, 2026-11-02). Given the comps on screen and the indices the server named
 * as `recommended` (the first TARGET_COMPS that can price a report, in Best match order), return the
 * comps with those ticked, or the SAME array when nothing should change:
 *   - the set no longer matches the rows that were ranked (`expected` rows),
 *   - anything is already ticked (the agent's own choice is never overridden), or
 *   - no recommended row can be ticked here (a field is still to fill).
 * A row with a field still to fill is never ticked on the agent's behalf. Display-only: no figure.
 * [Security review noted; owner sign-off pending on pre-ticking.]
 */
export function tickTopRecommended<T extends { _selected?: boolean; confirmed?: boolean }>(
  comps: T[], recommended: number[], expected: number,
): T[] {
  if (comps.length !== expected || comps.some((c) => c._selected)) return comps;
  const pick = new Set(recommended.filter((i) =>
    comps[i] && compMissingFields(comps[i] as unknown as Record<string, unknown>).length === 0));
  if (pick.size === 0) return comps;
  // `confirmed` travels WITH the tick (HOTFIX 2026-11-03). A search row arrives `confirmed: false`
  // and the server refuses any provider row that is not `confirmed: true` (400, "not explicitly
  // reviewed and selected"). Unticking clears both (ConfirmGrid), exactly as a hand tick sets both.
  return comps.map((c, i) => (pick.has(i) ? { ...c, _selected: true, confirmed: true } : c));
}

/**
 * A comp that is going out on a Generate is ticked on the review the agent just confirmed, so it is
 * reviewed and selected: send it `confirmed: true` (HOTFIX 2026-11-03). Only ever called on the
 * submitted (ticked, usable) list; it never ticks anything and never touches a figure.
 */
export function confirmTicked<T extends { confirmed?: boolean }>(c: T): T {
  return c.confirmed === true ? c : { ...c, confirmed: true };
}

/**
 * OWNER RULING 2026-11-03 (~11:05): moving forward is selection. At Continue, every comp still
 * ticked (an untouched pre-check, a reordered row, a corrected row) becomes `confirmed: true`;
 * an unticked row is left exactly as it is, so it never reaches the payload. No figure moves.
 */
export function confirmSelected<T extends { _selected?: boolean; confirmed?: boolean }>(comps: T[]): T[] {
  return comps.some((c) => c._selected && c.confirmed !== true)
    ? comps.map((c) => (c._selected ? confirmTicked(c) : c))
    : comps;
}
